import { useTranslation } from 'react-i18next';
import type { PerioDiagnosis } from '@/utils/perio';

// ── Stage / Grade chip colors ─────────────────────────────────────────────────

const stageColors: Record<string, string> = {
  I:   'bg-ds-green-tint text-ds-green-ink',
  II:  'bg-ds-amber-tint text-ds-amber-ink',
  III: 'bg-ds-amber-tint text-ds-amber-ink',
  IV:  'bg-ds-red-tint text-ds-red-ink',
};

const gradeColors: Record<string, string> = {
  A: 'bg-ds-teal-tint text-ds-teal',
  B: 'bg-ds-teal-tint text-ds-teal',
  C: 'bg-ds-red-tint text-ds-red-ink',
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface PerioDiagnosisCardProps {
  diagnosis: PerioDiagnosis;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PerioDiagnosisCard({ diagnosis }: PerioDiagnosisCardProps) {
  const { t } = useTranslation();

  if (!diagnosis.stage) {
    return (
      <div className="rounded-lg border border-ds-line bg-ds-card p-4">
        <p className="text-sm font-semibold text-ds-text">
          {t('dental.perio.diagnosis.title')}
        </p>
        <p className="mt-2 text-sm text-ds-muted">
          {t('dental.perio.diagnosis.title', 'Ingen parodontale problemer registreret.')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-ds-line bg-ds-card p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ds-muted">
        {t('dental.perio.diagnosis.title')}
      </p>

      <div className="flex flex-wrap gap-2">
        {diagnosis.stage && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-ds-muted">
              {t('dental.perio.diagnosis.stage')}
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-sm font-bold ${stageColors[diagnosis.stage] ?? ''}`}
            >
              {diagnosis.stage}
            </span>
          </div>
        )}

        {diagnosis.grade && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-ds-muted">
              {t('dental.perio.diagnosis.grade')}
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-sm font-bold ${gradeColors[diagnosis.grade] ?? ''}`}
            >
              {diagnosis.grade}
              {diagnosis.gradeIsDefault && (
                <span className="ml-0.5 text-xs font-normal opacity-70">*</span>
              )}
            </span>
          </div>
        )}
      </div>

      {diagnosis.summary && (
        <p className="mt-3 text-sm text-ds-text">{diagnosis.summary}</p>
      )}

      <p className="mt-3 rounded bg-ds-amber-tint px-2 py-1.5 text-xs text-ds-amber-ink">
        {t('dental.perio.diagnosis.disclaimer')}
      </p>
    </div>
  );
}
